import * as React from 'react';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '../utils/propStyles';
import { utils } from '@kurtosys/ksys-app-template';
import { SuperscriptMeta, AccessibilityStore } from '../stores';
import { getShadowRoot } from '../utils/shadowDomHelper';
import { KeyboardEventHelper } from '../utils/KeyboardEventHelper';
import { SuperscriptRefHelper } from '../utils/SuperscriptRefHelper';

const ListItem = styled<any, any>('li')`
    ${propStyles};
`;

ListItem.defaultProps = {
    display: 'flex',
    marginY: 'sm',
};

const DisclaimerCharacter = styled<any, any>('div')`
    ${propStyles};
    flex-shrink: 0;
`;

DisclaimerCharacter.defaultProps = {
    margin: '0 0.5em 0 0',
};

const DisclaimerContent = styled<any, any>('div')`
    ${propStyles};
`;

const DisclaimerReturnAnchor = styled<any, any>('a')`
    ${propStyles}
`;

DisclaimerContent.defaultProps = {};

export type Disclaimer = string | SuperscriptMeta;

interface DisclaimerListItemProps {
    disclaimer: Disclaimer;
    getHtmlElement: (instance: HTMLLIElement) => void;
    accessibilityStore?: AccessibilityStore;
    focus?: boolean;
    focusTime?: number;
    superscriptContext?: string;
    theme?: { [property: string]: string | number };
}

@inject('accessibilityStore')
@observer
class DisclaimerListItem extends React.Component<DisclaimerListItemProps, any> {
    element: HTMLLIElement;

    constructor(props) {
        super(props);
    }

    componentDidMount() {
        this.shouldFocus();
    }

    componentDidUpdate() {
        this.shouldFocus();
    }

    shouldFocus() {
        if (this.element && this.props.focus === true) {
            this.element.focus();
            this.element.scrollIntoView();
        }
    }

    getHtmlElement = (instance: HTMLLIElement) => {
        this.element = instance;
    };

    @computed
    get accessibilityStore() {
        const { accessibilityStore } = this.props;
        return accessibilityStore;
    }

    @computed
    get superscriptContext() {
        return !utils.typeChecks.isNullOrEmpty(this.props.superscriptContext)
            ? `-${this.props.superscriptContext}`
            : '';
    }

    @computed
    get footnoteId() {
        const { disclaimer } = this.props;
        const id =
            (typeof disclaimer !== 'string' && disclaimer.id) || undefined;
        return `${id}${this.superscriptContext}`;
    }

    @computed
    get superscript() {
        const { theme, disclaimer } = this.props;
        if (typeof disclaimer === 'string') {
            return null;
        }
        const ariaLabel =
            this.accessibilityStore &&
            this.accessibilityStore.superscriptAccessibilityValue(
                'footnoteSuperscript',
                'related superscript character {superscript}',
                {
                    superscript: disclaimer.superscript,
                }
            );
        return (
            <DisclaimerCharacter
                aria-label={ariaLabel}
                aria-describedby={this.footnoteId}
                {...theme.DisclaimerCharacter}
            >
                {disclaimer.superscript}
            </DisclaimerCharacter>
        );
    }

    @computed
    get text() {
        const { theme, disclaimer } = this.props;
        const text =
            typeof disclaimer === 'string' ? disclaimer : disclaimer.text;
        return (
            <DisclaimerContent
                id={`${this.footnoteId}`}
                tabIndex={'0'}
                ref={this.getHtmlElement}
                dangerouslySetInnerHTML={{
                    __html: text,
                }}
                {...theme.DisclaimerContent}
            />
        );
    }

    @computed
    get superscriptReturnLink() {
        const { theme, disclaimer } = this.props;
        if (typeof disclaimer === 'string' || !disclaimer.id) {
            return null;
        }
        const ariaLabel =
            this.accessibilityStore &&
            this.accessibilityStore.superscriptAccessibilityValue(
                'footnoteReturnLink',
                'Return to actioned superscript or first occurrence'
            );
        return (
            <DisclaimerReturnAnchor
                href={'#'}
                onClick={e => this.onReturnLinkClick(e, this.footnoteId)}
                onKeyUp={e => this.onReturnLinkKeyUp(e, this.footnoteId)}
                onKeyDown={e => KeyboardEventHelper.disableAcceptKeys(e)}
                aria-label={ariaLabel}
                aria-describedby={`#${this.footnoteId}`}
                {...theme.DisclaimerReturnAnchor}
            >
                &#8617;
            </DisclaimerReturnAnchor>
        );
    }

    onReturnLinkKeyUp(
        e: React.KeyboardEvent<HTMLAnchorElement>,
        footnoteId: string
    ) {
        if (KeyboardEventHelper.isAcceptKey(e)) {
            this.scrollToSuperscript(e, footnoteId);
        }
    }

    onReturnLinkClick(
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        footnoteId: string
    ) {
        this.scrollToSuperscript(e, footnoteId);
    }

    scrollToSuperscript(e: React.BaseSyntheticEvent, footnoteId: string) {
        if (footnoteId) {
            e.stopPropagation();
            e.preventDefault();
            const linkedRef = this.getSuperscriptRef(footnoteId);
            // scroll to the last actioned superscript
            const isScrolling = this.scrollToSelector(
                `[id^="${footnoteId}${linkedRef}"]`
            );
            if (!isScrolling) {
                // fallback to the first possible superscript for this footnote id
                this.scrollToSelector(`[id^="${footnoteId}"]`);
            }
        }
    }

    scrollToSelector(selector): boolean {
        // fallback to the first possible superscript
        const superscripts: NodeListOf<HTMLElement> = getShadowRoot().querySelectorAll(
            selector
        );
        if (superscripts && superscripts.length > 0) {
            superscripts[0].scrollIntoView();
            superscripts[0].focus();
            return true;
        }
        return false;
    }

    getSuperscriptRef(footnoteId: string) {
        if (SuperscriptRefHelper.hasRef(footnoteId)) {
            return SuperscriptRefHelper.getRef(footnoteId);
        }
        return '';
    }

    render() {
        const { theme } = this.props;
        return (
            <ListItem {...theme.DisclaimerListItem}>
                {this.superscript}
                {this.text}
                {this.superscriptReturnLink}
            </ListItem>
        );
    }
}

export default withTheme(DisclaimerListItem);



// WEBPACK FOOTER //
// ./src/Components/DisclaimerListItem.tsx